// Patch 27/28 · home "Top lojas": top 3 by tierTCG rating and top 3 in the community ranking, as slim rows.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { api, resolveImageUrl } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { entryPath } from "@/lib/paths";
import { entryImage } from "@/lib/placeholders";

const MEDAL = ["bg-amber-400", "bg-stone-300", "bg-[#C98A4B]"];

export function Skel({ className = "" }) {
  return <div className={`tier-skel ${className}`} aria-hidden="true" />;
}

function Row({ e, i, voted, right }) {
  return (
    <Link to={entryPath(e)} className="flex items-center gap-3 h-16 pr-3 bg-[#121216] border border-white/10 hover:border-amber-400/50 transition-colors" data-testid={`top-row-${e.id}`}>
      <span className={`self-stretch w-9 shrink-0 grid place-items-center font-display text-[20px] text-[#0A0A0C] ${MEDAL[i]}`}>#{i + 1}</span>
      <img src={resolveImageUrl(entryImage(e), 96)} alt="" className="w-11 h-11 object-cover rounded-sm bg-[#1b1c22] shrink-0" loading="lazy" />
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5">
          <span className="font-bold text-[15px] leading-tight text-stone-100 truncate">{e.name}</span>
          {voted && <Check className="w-4 h-4 text-emerald-400 shrink-0" aria-label="✓" data-testid="voted-tick" />}
        </span>
        <span className="block text-[13px] text-stone-400 truncate">{e.city}</span>
      </span>
      {right}
    </Link>
  );
}

function List({ over, title, to, more, children, testid }) {
  return (
    <section data-testid={testid}>
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.24em] font-bold text-amber-400">{over}</div>
          <h2 className="font-display text-[26px] sm:text-3xl font-black uppercase leading-none mt-1 text-stone-100">{title}</h2>
        </div>
        <Link to={to} className="text-[14px] text-amber-300 hover:text-amber-200 whitespace-nowrap pb-0.5">{more}</Link>
      </div>
      <div className="mt-3 space-y-2">{children}</div>
    </section>
  );
}

export default function HomeStrips() {
  const { t } = useI18n();
  const { user } = useAuth();
  const H = t.h27;
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/home/strips").then(({ data }) => setD(data)).catch(() => setD({ top_stores: [], top_community: [], voted: [] }));
  }, [user?.id]);
  const voted = new Set(d?.voted || []);
  if (!d)
    return (
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-6 grid gap-8 lg:grid-cols-2" data-testid="home-strips-loading">
        {[0, 1].map((k) => (
          <div key={k} className="space-y-2">
            <Skel className="h-3 w-24" />
            <Skel className="h-7 w-44" />
            {[0, 1, 2].map((r) => <Skel key={r} className="h-16 w-full" />)}
          </div>
        ))}
      </div>
    );
  if (!d.top_stores.length && !d.top_community.length) return null;
  return (
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-6 grid gap-8 lg:grid-cols-2" data-testid="home-strips">
      {!!d.top_stores.length && (
        <List over={H.top_over} title={H.top_title} to="/stores" more={H.see_all} testid="top-tiertcg">
          {d.top_stores.map((e, i) => (
            <Row key={e.id} e={e} i={i} voted={voted.has(e.id)}
              right={<span className="flex items-center gap-2 shrink-0"><span className="font-display text-lg leading-6 px-1.5 border border-amber-400 text-amber-300">{e.tier}</span><span className="text-[13px] text-stone-300 tabular-nums">{e.overall.toFixed(1)}</span></span>} />
          ))}
        </List>
      )}
      {!!d.top_community.length && (
        <List over={H.comm_over} title={H.comm_title} to="/stores?tab=community" more={H.see_all} testid="top-community">
          {d.top_community.map((e, i) => (
            <Row key={e.id} e={e} i={i} voted={voted.has(e.id)}
              right={<span className="text-right shrink-0 leading-tight"><span className="block text-[15px] font-bold text-emerald-300 tabular-nums">▲ {e.up}</span><span className="block text-[12px] text-stone-400 tabular-nums">{e.pct}% {H.positive}</span></span>} />
          ))}
        </List>
      )}
    </div>
  );
}
